import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { Dropdown } from '@framework/ui/components/dropdown/Dropdown.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { Link } from '@framework/ui/core/router/TypedRoute.tsx';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { matchPath, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { type RoutePath } from '../RoutePaths.ts';
import { useMainMenuItem } from './useMainMenuItem.tsx';

const MainMenuFrontWrapper = styled.nav``;

const MainMenuLoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  min-height: 54px;
  min-width: 54px;
  .anticon {
    color: white;
  }
`;

export enum MenuItemsType {
  LEAF = 'leaf',
  PARENT = 'parent',
}

export interface LeafMenuItemsProps {
  type: MenuItemsType.LEAF;
  menuKey: string;
  to: RoutePath;
  children: React.ReactNode;
  /**  Titolo descrittivo dell'azione per Screen Reader */
  title?: string;
  loading?: boolean;
  icon?: React.ReactNode;
}

export interface ParentMenuItemsProps {
  type: MenuItemsType.PARENT;
  menuKey: string;
  children: React.ReactNode;
  /**  Titolo descrittivo dell'azione per Screen Reader */
  title?: string;
  loading?: boolean;
  icon?: React.ReactNode;
  subMenu: LeafMenuItemsProps[];
}

export type MenuItemsProps = ParentMenuItemsProps | LeafMenuItemsProps;

type IMainMenuProps = object;

export function MainMenu(props: IMainMenuProps) {
  const { t } = useTranslation();

  const items = useMainMenuItem();

  return (
    <MainMenuFrontWrapper
      aria-label={t('main-menu.mobile.aria-label', 'Menu Principale')}
      id="main-menu"
    >
      <Space size={48} className="main-menu-wrapper-space">
        {items.map(item => {
          if (item.loading)
            return (
              <MainMenuLoadingWrapper
                key={item.menuKey}
                aria-label={t(
                  'main-menu.mobile.loading.aria-label',
                  'Caricamento',
                )}
              >
                <LoaderSpin noPadding size="default" />
              </MainMenuLoadingWrapper>
            );

          switch (item.type) {
            case MenuItemsType.LEAF:
              return (
                <NavLink
                  key={item.menuKey}
                  to={item.to}
                  activeClassName="active"
                  title={item.title}
                >
                  {item.children}
                </NavLink>
              );

            case MenuItemsType.PARENT: {
              const menu = {
                items: item.subMenu.map(sub => ({
                  key: sub.menuKey,
                  label: <Link to={sub.to}>{sub.children}</Link>,
                  title: sub.title,
                  icon: sub.icon,
                  theme: 'light',
                })),
              };

              return (
                <Dropdown
                  overlayClassName="main-menu-dropdown-overlay"
                  key={item.menuKey}
                  menu={menu}
                >
                  <NavLink
                    key={item.menuKey}
                    to="#"
                    onClick={e => e.preventDefault()}
                    title={item.title}
                    activeClassName="active"
                    // La voce padre è attiva quando la route corrente
                    // corrisponde a una delle voci del suo sottomenù
                    isActive={(_, location) =>
                      item.subMenu.some(
                        sub => !!matchPath(location.pathname, { path: sub.to }),
                      )
                    }
                  >
                    {item.children}
                  </NavLink>
                </Dropdown>
              );
            }
          }
          assertNever(item);
        })}
      </Space>
    </MainMenuFrontWrapper>
  );
}
